import React from "react";
import { useTranslation } from "react-i18next";
import { Table, ColumnDef } from "@tanstack/react-table";
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
} from "@heroui/react";

type DropDownProps<TData> = {
  table: Table<TData>;
};

export default function DropDown<TData>({ table }: DropDownProps<TData>) {
  const { t } = useTranslation();

  const selectedKeys = table
    .getAllColumns()
    .filter((column) => column.getIsVisible())
    .map((column) => column.id);

  const handleItemClick = (key: React.Key) => {
    const column = table.getColumn(key.toString());
    if (!column) return;

    column.toggleVisibility(!column.getIsVisible());
  };

  return (
    <Dropdown>
      <DropdownTrigger>
        <Button
          color="primary"
          variant="bordered"
          className="capitalize"
          radius="full"
          size="sm"
        >
          {t("table.columns")}
        </Button>
      </DropdownTrigger>

      <DropdownMenu
        aria-label={t("table.columnsMenu.ariaLabel")}
        variant="flat"
        closeOnSelect={false}
        disallowEmptySelection
        selectionMode="multiple"
        color="primary"
        classNames={{
          base: "max-h-[420px]",
          list: "max-h-[380px] overflow-y-auto",
        }}
        selectedKeys={selectedKeys}
        onAction={handleItemClick}
      >
        {table
          .getAllColumns()
          .filter(
            (column) => column.getCanHide() && !column.id.startsWith("actions"),
          )
          .map((column) => (
            <DropdownItem key={column.id}>
              <span className="text-xs">
                {renderHeader(column.columnDef.header, {})}
              </span>
            </DropdownItem>
          ))}
      </DropdownMenu>
    </Dropdown>
  );
}

const renderHeader = (header: ColumnDef<any>["header"], context: any) => {
  if (!header) return null;

  if (typeof header === "function") {
    const headerContent = header(context);
    if (typeof headerContent === "string") {
      return headerContent;
    }

    if (React.isValidElement(headerContent)) {
      const children = (headerContent.props as React.PropsWithChildren<{}>)
        .children;

      if (typeof children === "string") return children;
    }

    return null;
  }

  return header;
};
